export const SITE_BUILDER_INNER_BLOCK_TYPES = [
    "text",
    "card",
    "button",
    "clientPortalButton",
    "separator",
    "image",
    "video",
    "icon",
    "accordion",
    "alert",
    "iconList",
    "rating",
    "share",
    "socialMedia",
    "searchBar",
    "textHighlight",
    "chart",
    "progressBar",
    "badge",
    "ctaBadge",
    "avatars",
    "blockquote",
    "form",
    "countdown",
    "code",
    "map",
    "donationButton",
    "addToCartButton",
    "supportedPaymentButton",
] as const;

export const SITE_BUILDER_STRUCTURE_BLOCK_TYPES = [
    "richContent",
    "column",
] as const;

/**
 * Inner/text-flow blocks that Web and Shop place inside one movable Column.
 * Both module adapters consume this neutral contract so their canvas
 * selection, drag and resize behavior stays identical.
 */
export const SITE_BUILDER_TEXT_COLUMN_BLOCK_TYPES = [
    "badge",
    "blockquote",
    "chart",
    "ctaBadge",
    "form",
    "heading",
    "iconList",
    "link",
    "paragraph",
    "progressBar",
    "rating",
    "searchBar",
    "share",
    "socialMedia",
    "text",
    "textHighlight",
] as const;

const SITE_BUILDER_TEXT_COLUMN_GRID_OPTIONS: Readonly<
    Record<string, Readonly<{ gridH: number; gridW: number }>>
> = {
    badge: { gridH: 2, gridW: 2 },
    blockquote: { gridH: 3, gridW: 5 },
    chart: { gridH: 7, gridW: 6 },
    ctaBadge: { gridH: 2, gridW: 6 },
    form: { gridH: 8, gridW: 6 },
    progressBar: { gridH: 1, gridW: 4 },
    rating: { gridH: 2, gridW: 3 },
    searchBar: { gridH: 2, gridW: 6 },
    share: { gridH: 3, gridW: 6 },
    socialMedia: { gridH: 3, gridW: 6 },
    textHighlight: { gridH: 2, gridW: 6 },
};

export type SiteBuilderInnerBlockType = typeof SITE_BUILDER_INNER_BLOCK_TYPES[number];
export type SiteBuilderStructureBlockType = typeof SITE_BUILDER_STRUCTURE_BLOCK_TYPES[number];
export type SiteBuilderInnerBlockFootprint = Readonly<{
    h: number;
    w: number;
}>;

export const SITE_BUILDER_INNER_BLOCK_FOOTPRINTS = {
    accordion: { w: 6, h: 5 },
    addToCartButton: { w: 2, h: 1 },
    alert: { w: 4, h: 1 },
    avatars: { w: 3, h: 1 },
    badge: { w: 2, h: 1 },
    blockquote: { w: 5, h: 3 },
    button: { w: 2, h: 1 },
    clientPortalButton: { w: 2, h: 1 },
    card: { w: 4, h: 4 },
    chart: { w: 6, h: 7 },
    code: { w: 2, h: 1 },
    countdown: { w: 3, h: 2 },
    ctaBadge: { w: 6, h: 1 },
    donationButton: { w: 2, h: 1 },
    form: { w: 6, h: 7 },
    icon: { w: 2, h: 1 },
    iconList: { w: 4, h: 2 },
    image: { w: 4, h: 4 },
    map: { w: 5, h: 3 },
    progressBar: { w: 4, h: 1 },
    rating: { w: 3, h: 2 },
    searchBar: { w: 6, h: 1 },
    separator: { w: 12, h: 1 },
    share: { w: 6, h: 1 },
    socialMedia: { w: 6, h: 1 },
    supportedPaymentButton: { w: 4, h: 1 },
    text: { w: 4, h: 2 },
    textHighlight: { w: 6, h: 2 },
    video: { w: 5, h: 3 },
} as const satisfies Record<
    SiteBuilderInnerBlockType,
    SiteBuilderInnerBlockFootprint
>;

export function siteBuilderInnerBlockFootprint(
    type: SiteBuilderInnerBlockType,
): SiteBuilderInnerBlockFootprint {
    return SITE_BUILDER_INNER_BLOCK_FOOTPRINTS[type];
}

export function siteBuilderTextColumnGridOptions(
    type: string,
): Readonly<Record<string, number>> {
    return SITE_BUILDER_TEXT_COLUMN_GRID_OPTIONS[type] ?? {};
}

export function siteBuilderUsesTextColumn(type: string): boolean {
    return (
        SITE_BUILDER_TEXT_COLUMN_BLOCK_TYPES as readonly string[]
    ).includes(type);
}

export function isSiteBuilderInnerBlockType(value: string): value is SiteBuilderInnerBlockType {
    return (SITE_BUILDER_INNER_BLOCK_TYPES as readonly string[]).includes(value);
}

export function isSiteBuilderStructureBlockType(value: string): value is SiteBuilderStructureBlockType {
    return (SITE_BUILDER_STRUCTURE_BLOCK_TYPES as readonly string[]).includes(value);
}
